<script setup lang="ts">
  import { Money3Component as InputMoney } from 'v-money3'
  import { computed, useId } from 'vue'

  const props = defineProps<{
    id?: string,
    modelValue: number,
    max?: number,
    min?: number,
    label?: string,
    required?: boolean,
    disabled?: boolean,
    readonly?: boolean,
    placeholder?: string,
    error?: string[],
    prefix?: string,
    suffix?: string,
    maxBy?:
      | 'tinyint'
      | 'utinyint'
      | 'smallint'
      | 'usmallint'
      | 'mediumint'
      | 'umediumint'
      | 'int'
      | 'uint'
      | 'bigint',
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void,
  }>()

  // https://github.com/jonathanpmartins/v-money3#properties
  const options = computed(() => ({
    decimal: ',',
    thousands: '.',
    precision: 0,
    min: props.min ?? 0,
    max: limitMax.value,
  }))

  const limitMax = computed(() => {
    if (props.max) {
      return props.max
    }

    const maxBy = props.maxBy ?? 'uint'
    switch (maxBy) {
    case 'tinyint': return 100 // 127
    case 'utinyint': return 255 // 255
    case 'smallint': return 32_000 // 32,767
    case 'usmallint': return 65_000 // 65,535
    case 'mediumint': return 8_000_000 // 8,388,607
    case 'umediumint': return 16_000_000 // 16,777,215
    case 'int': return 2_000_000_000 // 2,147,483,647
    case 'uint': return 4_000_000_000 // 4,294,967,295
    case 'bigint': return Number.MAX_SAFE_INTEGER
    default: return 4_000_000_000 // same as uint
    }
  })

  const textInput = computed({
    set: (val) => {
      emit('update:modelValue', Number(val))
    },
    get: () => props.modelValue,
  })

  const defaultId = useId()
  const inputId = computed(() => props.id ?? defaultId)
</script>

<template>
  <div>
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-red-600">
        *
      </span>
    </label>
    <div class="relative flex items-center">
      <input-money
        v-bind="options"
        :id="inputId"
        v-model="textInput"
        :readonly="readonly"
        :disabled="disabled"
        class="form-input" />
      <span
        v-if="prefix"
        class="prefix">
        {{ prefix }}
      </span>
      <span
        v-if="suffix"
        class="suffix">
        {{ suffix }}
      </span>
    </div>
    <app-error-msg :msg="error" />
  </div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .prefix {
    @apply absolute left-4 text-gray-400 pointer-events-none text-sm;
  }
  .input-has-text .prefix.suffix {
    @apply inline;
  }

  .suffix {
    @apply absolute right-4 text-gray-400 pointer-events-none text-sm;
  }

  .form-input:has(+ span.prefix) {
    @apply pl-10;
  }
  .form-input:has(+ span.suffix) {
    @apply pr-10;
  }

  .text-right .form-input {
    @apply text-right;
  }
</style>
